import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static data cache
let staticVillasCache = null;
let staticSettingsCache = null;

// Load static JSON from /data folder (generated at build time)
const loadStaticData = async (filename) => {
  try {
    const response = await fetch(`/data/${filename}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.log(`Static ${filename} not available`);
  }
  return null;
};

// ========== SWR PATTERN: Static first, then API ==========

// Get static villas immediately (synchronous from cache or async load)
export const getStaticVillas = async () => {
  if (staticVillasCache) return staticVillasCache;
  staticVillasCache = await loadStaticData('villas.json');
  return staticVillasCache || [];
};

// Get villas from live API (no timeout - let it take as long as needed)
export const getLiveVillas = async (featured = null) => {
  try {
    const params = featured !== null ? { featured } : {};
    const response = await axios.get(`${API}/villas`, { params });
    return response.data;
  } catch (error) {
    console.log('Live API unavailable');
    return null; // Return null to indicate API failed
  }
};

// Legacy: getVillas with fallback (for backwards compatibility)
export const getVillas = async (featured = null) => {
  // First try static
  const staticData = await getStaticVillas();
  if (staticData && staticData.length > 0) {
    // Return static immediately, API will be called separately
    if (featured !== null) {
      return staticData.filter(v => v.is_featured === featured);
    }
    return staticData;
  }
  
  // If no static, try API with timeout
  try {
    const params = featured !== null ? { featured } : {};
    const response = await axios.get(`${API}/villas`, { params, timeout: 10000 });
    return response.data;
  } catch (error) {
    console.log('API unavailable, no static data available');
    return [];
  }
};

// Get static villa by ID/slug
export const getStaticVilla = async (id) => {
  const villas = await getStaticVillas();
  if (villas && villas.length > 0) {
    return villas.find(v => v.id === id || v.slug === id) || null;
  }
  return null;
};

// Get live villa from API
export const getLiveVilla = async (id) => {
  try {
    const response = await axios.get(`${API}/villas/${id}`);
    return response.data;
  } catch (error) {
    console.log('Live API unavailable for villa');
    return null;
  }
};

// Legacy: getVilla with fallback
export const getVilla = async (id) => {
  // First try static
  const staticVilla = await getStaticVilla(id);
  if (staticVilla) return staticVilla;
  
  // Then try API
  try {
    const response = await axios.get(`${API}/villas/${id}`, { timeout: 10000 });
    return response.data;
  } catch (error) {
    console.log('API unavailable, villa not found in static data');
    return null;
  }
};

// Get static settings
export const getStaticSettings = async () => {
  if (staticSettingsCache) return staticSettingsCache;
  staticSettingsCache = await loadStaticData('settings.json');
  return staticSettingsCache || null;
};

// Get live settings from API
export const getLiveSettings = async () => {
  try {
    const response = await axios.get(`${API}/settings`);
    return response.data;
  } catch (error) {
    console.log('Live API unavailable for settings');
    return null;
  }
};

// Legacy: getSiteSettings with fallback
export const getSiteSettings = async () => {
  // First try static
  const staticSettings = await getStaticSettings();
  if (staticSettings) return staticSettings;
  
  // Then try API
  try {
    const response = await axios.get(`${API}/settings`, { timeout: 10000 });
    return response.data;
  } catch (error) {
    console.log('API unavailable, no static settings');
    return null;
  }
};

import { collectUserInfo, initUTMTracking } from './userInfo';

// Initialize UTM tracking on load
if (typeof window !== 'undefined') {
  initUTMTracking();
}

export const submitContact = async (data, options = {}) => {
  // Collect user info and merge with form data
  const userInfo = collectUserInfo({
    villaId: data.villa_id || options.villaId,
    villaName: data.villa_name || options.villaName,
  });
  
  const enrichedData = {
    ...data,
    ...userInfo,
  };
  
  const response = await axios.post(`${API}/contact`, enrichedData);
  return response.data;
};

// Admin API
export const adminGetVillas = async () => {
  const response = await axios.get(`${API}/admin/villas`);
  return response.data;
};

export const adminCreateVilla = async (data) => {
  const response = await axios.post(`${API}/admin/villas`, data);
  return response.data;
};

export const adminUpdateVilla = async (id, data) => {
  const response = await axios.put(`${API}/admin/villas/${id}`, data);
  return response.data;
};

export const adminDeleteVilla = async (id) => {
  const response = await axios.delete(`${API}/admin/villas/${id}`);
  return response.data;
};

export const adminGetContacts = async () => {
  const response = await axios.get(`${API}/admin/contacts`);
  return response.data;
};

export const adminMarkContactRead = async (id) => {
  const response = await axios.put(`${API}/admin/contacts/${id}/read`);
  return response.data;
};

// Image API
export const adminUploadImage = async (villaId, file, category = 'exterior', onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/villas/${villaId}/images?category=${category}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined,
  });
  return response.data;
};

export const getImageCategories = async () => {
  const response = await axios.get(`${API}/image-categories`);
  return response.data;
};

export const adminDeleteImage = async (villaId, imageId) => {
  const response = await axios.delete(`${API}/admin/villas/${villaId}/images/${imageId}`);
  return response.data;
};

export const adminUpdateImageOrder = async (villaId, imageIds) => {
  const response = await axios.put(`${API}/admin/villas/${villaId}/images/order`, {
    image_ids: imageIds,
  });
  return response.data;
};

// Main Image API
export const adminUploadMainImage = async (villaId, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/villas/${villaId}/main-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percentCompleted);
    } : undefined,
  });
  return response.data;
};

export const adminDeleteMainImage = async (villaId) => {
  const response = await axios.delete(`${API}/admin/villas/${villaId}/main-image`);
  return response.data;
};

// Admin PDF Upload
export const adminUploadPDF = async (villaId, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API}/admin/villas/${villaId}/pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  });
  return response.data;
};

export const adminDeletePDF = async (villaId) => {
  const response = await axios.delete(`${API}/admin/villas/${villaId}/pdf`);
  return response.data;
};

// Admin Settings API
export const adminGetSettings = async () => {
  const response = await axios.get(`${API}/admin/settings`);
  return response.data;
};

export const adminUpdateSettings = async (data) => {
  const response = await axios.put(`${API}/admin/settings`, data);
  return response.data;
};

// Helper to get image URL with Cloudinary optimization
export const getImageUrl = (image, size = 'full') => {
  if (!image) return null;
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // External URL (http/https) - Cloudinary
  if (image.url && image.url.startsWith('http')) {
    // Cloudinary URL - add optimization transformations
    if (image.url.includes('cloudinary.com')) {
      const url = image.url;
      const uploadIndex = url.indexOf('/upload/');
      if (uploadIndex !== -1) {
        const baseUrl = url.slice(0, uploadIndex + 8);
        const imagePath = url.slice(uploadIndex + 8);
        
        const transforms = {
          thumb: 'w_200,h_200,c_fill,q_auto,f_auto',
          medium: 'w_800,h_600,c_fill,q_auto,f_auto',
          full: 'w_1600,q_auto,f_auto'
        };
        
        return `${baseUrl}${transforms[size] || transforms.full}/${imagePath}`;
      }
    }
    return image.url;
  }
  
  // Internal URL starting with /api
  if (image.url && image.url.startsWith('/api')) {
    return `${backendUrl}${image.url}`;
  }
  
  // Internal URL (other)
  if (image.url) {
    return `${backendUrl}${image.url}`;
  }
  
  // Storage path
  if (image.storage_path) {
    if (image.storage_path.startsWith('/api')) {
      return `${backendUrl}${image.storage_path}`;
    }
    return `${backendUrl}/api/images/${image.storage_path}`;
  }
  
  return null;
};

// ========== ADMIN PUBLISH ===========

export const triggerPublish = async () => {
  const token = localStorage.getItem('villa-token');
  const response = await axios.post(`${API}/admin/publish`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};
